/**
 * Test Mode Helper for Notification & Error Testing
 * 
 * Allows simulating error conditions via query params:
 * - ?simulate=offline  → Forces offline state
 * - ?simulate=500      → Simulates backend 500 errors
 * - ?simulate=timeout  → Simulates network timeout
 * - ?simulate=clear    → Clears all simulations
 * 
 * Usage in development:
 * 1. Add ?simulate=offline to URL to test offline UI
 * 2. Add ?simulate=500 to test ErrorBanner with backend errors
 * 3. Use the TestModeIndicator component in dev builds
 */

export type SimulationMode = 'offline' | '500' | 'timeout' | 'slow' | null;

// Global simulation state
let currentSimulation: SimulationMode = null;
let simulationListeners: Set<(mode: SimulationMode) => void> = new Set();

/**
 * Initialize test mode from URL params
 * Call this once on app startup
 */
export const initTestMode = (): void => {
  if (typeof window === 'undefined') return;
  
  const params = new URLSearchParams(window.location.search);
  const simulate = params.get('simulate');
  
  if (simulate === 'clear') {
    clearSimulation();
    // Remove the param from URL
    params.delete('simulate');
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  } else if (simulate === 'offline' || simulate === '500' || simulate === 'timeout' || simulate === 'slow') {
    setSimulation(simulate);
    console.log(`🧪 [TestMode] Simulation active: ${simulate}`);
  }
};

/**
 * Get current simulation mode
 */
export const getSimulation = (): SimulationMode => currentSimulation;

/**
 * Set simulation mode
 */
export const setSimulation = (mode: SimulationMode): void => {
  currentSimulation = mode;
  simulationListeners.forEach(listener => listener(mode));
  
  if (mode) {
    console.log(`🧪 [TestMode] Simulation set: ${mode}`);
  }
};

/**
 * Clear all simulations
 */
export const clearSimulation = (): void => {
  currentSimulation = null;
  simulationListeners.forEach(listener => listener(null));
  console.log('🧪 [TestMode] Simulation cleared');
};

/**
 * Subscribe to simulation changes
 */
export const subscribeToSimulation = (listener: (mode: SimulationMode) => void): (() => void) => {
  simulationListeners.add(listener);
  return () => simulationListeners.delete(listener);
};

/**
 * Check if we should simulate offline
 */
export const isSimulatingOffline = (): boolean => currentSimulation === 'offline';

/**
 * Check if we should simulate backend errors
 */
export const isSimulating500 = (): boolean => currentSimulation === '500';

/**
 * Check if we should simulate timeout
 */
export const isSimulatingTimeout = (): boolean => currentSimulation === 'timeout';

/**
 * Check if we should simulate slow network
 */
export const isSimulatingSlow = (): boolean => currentSimulation === 'slow';

/**
 * Wrapper for fetch/RPC calls that respects simulation mode
 * Use this in safeSupabase.ts or similar wrappers
 */
export const maybeSimulateError = async <T>(
  operation: () => Promise<T>
): Promise<T> => {
  // Simulate offline
  if (isSimulatingOffline()) {
    throw new Error('Simulated offline: Network unavailable');
  }
  
  // Simulate 500 error
  if (isSimulating500()) {
    throw new Error('Simulated 500: Internal Server Error');
  }
  
  // Simulate timeout
  if (isSimulatingTimeout()) {
    await new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Simulated timeout: Request timed out')), 100);
    });
  }
  
  // Simulate slow network (3s delay)
  if (isSimulatingSlow()) {
    await new Promise(resolve => setTimeout(resolve, 3000));
  }
  
  return operation();
};

/**
 * Get available simulation modes for UI
 */
export const getAvailableSimulations = (): { value: SimulationMode; label: string; description: string }[] => [
  { value: null, label: 'None', description: 'Normal operation' },
  { value: 'offline', label: 'Offline', description: 'Simulates no network connection' },
  { value: '500', label: '500 Error', description: 'Simulates backend errors' },
  { value: 'timeout', label: 'Timeout', description: 'Simulates request timeout' },
  { value: 'slow', label: 'Slow Network', description: 'Adds 3s delay to requests' },
];
